/**
 * 序列处理相关工具函数
 */

/**
 * 判断输入文本是否为FASTA格式
 * @param text 输入文本
 * @returns 是否为FASTA格式
 */
export const isFastaFormat = (text: string): boolean => {
  if (!text) return false;
  const lines = text.split('\n');
  return lines.some(line => line.trim().startsWith('>'));
};

/**
 * 判断输入文本是否为多序列FASTA格式
 * @param text 输入文本
 * @returns 是否为多序列FASTA格式
 */
export const isMultiFastaFormat = (text: string): boolean => {
  if (!text) return false;
  const lines = text.split('\n');
  let headerCount = 0;
  
  for (const line of lines) {
    if (line.trim().startsWith('>')) {
      headerCount++;
      if (headerCount > 1) return true;
    }
  }
  
  return false;
};

/**
 * 判断输入文本是否为单序列FASTA格式
 * @param text 输入文本
 * @returns 是否为单序列FASTA格式
 */
export const isSingleFastaFormat = (text: string): boolean => {
  if (!text) return false;
  const lines = text.split('\n');
  // 只有一行以'>'开头，并且不是多序列FASTA
  const headerCount = lines.filter(line => line.trim().startsWith('>')).length;
  return headerCount === 1;
};

/**
 * 从FASTA格式中提取单个序列（非多序列情况）
 * @param text FASTA格式文本
 * @returns 序列对象，包含ID和序列内容
 */
export const extractSingleFastaSequence = (text: string): { id: string; sequence: string } => {
  if (!text || !isSingleFastaFormat(text)) {
    return { id: '', sequence: text.replace(/\s/g, '') };
  }
  
  const lines = text.split('\n');
  let headerId = '';
  let sequence = '';
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('>')) {
      headerId = trimmedLine;
    } else if (trimmedLine.length > 0) {
      sequence += trimmedLine;
    }
  }
  
  return {
    id: headerId,  // 保留完整的ID，包括>符号
    sequence: sequence
  };
};

/**
 * 解析FASTA格式文本，返回序列列表
 * @param content FASTA格式文本
 * @returns 序列对象数组，包含ID和序列内容
 */
export const parseFasta = (content: string): { originalId: string; sequence: string }[] => {
  if (!content) return [];
  
  const lines = content.split('\n');
  const sequences: { originalId: string; sequence: string }[] = [];
  let currentOriginalId = '';
  let currentSequence = '';
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine.length === 0) return;
    
    if (trimmedLine.startsWith('>')) {
      if (currentSequence) {
        sequences.push({ originalId: currentOriginalId || '>sequence', sequence: currentSequence });
      }
      currentOriginalId = trimmedLine;
      currentSequence = '';
    } else {
      currentSequence += trimmedLine.replace(/\s/g, '');
    }
  });
  
  if (currentSequence) {
    sequences.push({ originalId: currentOriginalId || '>sequence', sequence: currentSequence });
  }
  
  return sequences;
};

/**
 * 将多序列分批处理，每批最多处理指定数量的序列
 * @param sequences 序列数组
 * @param batchSize 每批最大序列数
 * @returns 分批后的序列数组
 */
export const batchSequences = <T>(sequences: T[], batchSize: number): T[][] => {
  const batches: T[][] = [];
  for (let i = 0; i < sequences.length; i += batchSize) {
    batches.push(sequences.slice(i, i + batchSize));
  }
  return batches;
};

/**
 * 将序列对象列表转换为FASTA格式文本
 * @param sequences 序列对象数组
 * @returns FASTA格式文本
 */
export const sequencesToFasta = (sequences: { originalId: string; sequence: string }[]): string => {
  return sequences.map(seq => {
    const id = seq.originalId.startsWith('>') ? seq.originalId : `>${seq.originalId}`;
    return `${id}\n${seq.sequence}`;
  }).join('\n');
}; 